/**
 * SpaceCreatedItemBlock
 *
 * Compact message-local confirmation for a Space item created from chat.
 *
 * Primary responsibilities:
 * - Show the created Space item beside the source message
 * - Open the same Space item detail flow used by Space cards
 *
 * Dependencies:
 * - Add to Space action types
 * - Sidekick Space item open event
 */

'use client'

import {
  IconCheckbox,
  IconChevronRight,
  IconNotes
} from '@tabler/icons-react'

import {
  openCreatedSpaceNote,
  openCreatedSpaceTask,
  type CreatedSpaceNote,
  type CreatedSpaceTask,
  type SpaceNoteDestination
} from '@/components/messages/add-to-space-action'
import { cn } from '@/lib/utils'

type CreatedSpaceItem =
  | {
      type: 'note'
      item: CreatedSpaceNote
    }
  | {
      type: 'task'
      item: CreatedSpaceTask
    }

interface SpaceCreatedItemBlockProps {
  className?: string
  createdItem: CreatedSpaceItem
  destination: SpaceNoteDestination
}

const getItemCopy = (createdItem: CreatedSpaceItem) => {
  if (createdItem.type === 'task') {
    return {
      label: 'task',
      title: createdItem.item.title,
      icon: IconCheckbox
    }
  }

  return {
    label: 'note',
    title: createdItem.item.title,
    icon: IconNotes
  }
}

const openCreatedItem = (createdItem: CreatedSpaceItem) => {
  if (createdItem.type === 'task') {
    openCreatedSpaceTask(createdItem.item)
    return
  }

  openCreatedSpaceNote(createdItem.item)
}

// --------------

export function SpaceCreatedItemBlock({
  className,
  createdItem,
  destination
}: SpaceCreatedItemBlockProps) {
  const copy = getItemCopy(createdItem)
  const ItemIcon = copy.icon

  return (
    <button
      type="button"
      className={cn(
        'flex w-full max-w-md items-center gap-2 rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
      onClick={() => openCreatedItem(createdItem)}
      aria-label={`Open Space ${copy.label} ${copy.title}`}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background">
        <ItemIcon size={16} className="text-primary" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{copy.title}</span>
        <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
          <span className="truncate">{destination.displayPath}</span>
        </span>
      </span>
      <IconChevronRight size={16} className="shrink-0 text-muted-foreground" />
    </button>
  )
}
